/**
 * Declaración de las columnas de la grilla de Tickets, para <GridColumnSelector /> y
 * `useColumnVisibility` (src/commons/grid/).
 *
 * Vive en su propio módulo por lo mismo que `ticketsFilterFields.js`: lo consumen el
 * selector (para listar) y la grilla (para decidir qué celda se ve).
 *
 * TRES REGLAS al tocar esta lista:
 *
 * 1. El ORDEN es el del `thead`. No cambia nada funcional —cada celda se oculta sola, por
 *    nombre— pero es el orden en que el usuario ve las opciones en el selector, y que no
 *    coincida con la grilla se nota.
 *
 * 2. El `name` de una columna gateada por config tiene que ser LA MISMA clave que usa
 *    `columnsEnabled` en las celdas (`show={columnasVisibles.centroCostos}`). Es lo que
 *    permite que el mapa que devuelve el hook se consuma directo, sin traducir nombres.
 *
 * 3. El `messageId` es el MISMO del encabezado de la columna. Si el selector dijera algo
 *    distinto de lo que dice el `th`, el usuario no podría relacionarlos.
 */

export const TICKETS_COLUMNAS_STORAGE_KEY = 'TicketsGrid_columnas';

/**
 * @param columnsEnabled  los flags de config de TicketsGrid::buildColumnsEnabled
 *
 * `fixed: true` = no se puede ocultar y no aparece en el selector. Es el equivalente del
 * `:not(.no-colvis):not(:empty)` de las grillas de DataTables: las columnas sin título
 * (el checkbox de selección masiva, el indicador de color) aparecerían como casillas sin
 * etiqueta, y la de acciones no debe poder ocultarse — el usuario perdería los botones de
 * la fila sin una forma evidente de recuperarlos.
 */
export const buildTicketsColumns = (columnsEnabled) => {
	const flags = columnsEnabled || {};

	return [
		{ name: 'seleccion', fixed: true },
		{ name: 'ticketTipo', messageId: 'ticketsGrid.column_type.label_tipo', label: 'Tipo' },
		{ name: 'id', messageId: 'ticketsGrid.column_Tkt.label_tkt', label: 'Tkt' },
		{
			name: 'clientExternalTicketId',
			messageId: 'ticketsGrid.column.label_clientExternalTicketId',
			label: 'OS',
			enabled: flags.clientExternalTicketId === true,
		},
		{ name: 'indicador', fixed: true },
		{ name: 'estado', messageId: 'ticketsGrid.column_state.label_estado', label: 'Estado' },
		{
			name: 'prioridad',
			messageId: 'ticketsGrid.column_priority.label_prioridad',
			label: 'Prioridad',
			enabled: flags.prioridad === true,
		},
		{
			name: 'nivelAprobacionPresupuesto',
			messageId: 'ticketsGrid.column_state.label_nivelAprobacionPresupuesto',
			label: 'Nivel Aprobación',
			enabled: flags.nivelAprobacionPresupuesto === true,
		},
		{ name: 'fechaHoraAlta', messageId: 'ticketsGrid.column_create.label_creado', label: 'Creado' },
		{ name: 'usuarioCreacion', messageId: 'ticketsGrid.column_usuario_creacion.label_por', label: 'Por' },
		{ name: 'detalle', messageId: 'ticketsGrid.column_detail.label_detalle', label: 'Detalle' },
		{ name: 'servicio', messageId: 'ticketsGrid.column_service.label_servicio', label: 'Servicio' },
		{ name: 'entidad', messageId: 'ticketsGrid.column_entity.label_entidad', label: 'Entidad' },
		{ name: 'vin', messageId: 'ticketsGrid.column_vin.label_vin', label: 'VIN' },
		{ name: 'unidad', messageId: 'ticketsGrid.column_unidad.label_unidad', label: 'Unidad' },
		{
			name: 'transportadora',
			messageId: 'ticketsGrid.column.label_transportadora',
			label: 'Transportadora',
			enabled: flags.transportadora === true,
		},
		{
			name: 'rentadora',
			messageId: 'ticketsGrid.column.label_rentadora',
			label: 'Rentadora',
			enabled: flags.rentadora === true,
		},
		{ name: 'gerenciador', messageId: 'ticketsGrid.column_gerenciador.label_gerenciador', label: 'Gerenciador' },
		{
			name: 'mostrarTotalPresupuesto',
			messageId: 'ticketsGrid.column_total.label_total',
			label: 'Total del Presupuesto',
			enabled: flags.mostrarTotalPresupuesto === true,
		},
		{
			name: 'region',
			messageId: 'ticketsGrid.column_region.label_region',
			label: 'Región',
			enabled: flags.region === true,
		},
		{ name: 'subRegion', messageId: 'ticketsGrid.column_region.label_subRegion', label: 'SubRegión' },
		{ name: 'base', messageId: 'ticketsGrid.column_base.label_base', label: 'Base' },
		{
			name: 'pais',
			messageId: 'ticketsGrid.column_pais.label_pais',
			label: 'Pais',
			enabled: flags.pais === true,
		},
		{
			name: 'centroCostos',
			messageId: 'ticketsGrid.column_cost_center.label_centro_de_costos',
			label: 'Centro de Costos',
			enabled: flags.centroCostos === true,
		},
		{
			// El encabezado dice "Titular" aunque la columna sea el centro de beneficios:
			// se respeta la key del th para que el selector diga lo mismo que la grilla.
			name: 'centroBeneficios',
			messageId: 'ticketsGrid.column_titular.label_titular',
			label: 'Titular',
			enabled: flags.centroBeneficios === true,
		},
		{
			name: 'responsable1',
			messageId: 'Responsable',
			label: 'Responsable',
			enabled: flags.responsable1 === true,
		},
		{ name: 'enTaller', messageId: 'Taller', label: 'Taller' },
		{
			name: 'fechaPrometida',
			messageId: 'Fecha_promesa',
			label: 'Fecha promesa',
			enabled: flags.fechaPrometida === true,
		},
		{
			name: 'diasReparacion',
			messageId: 'Dias_reparacion',
			label: 'Días de Reparación',
			enabled: flags.diasReparacion === true,
		},
		{ name: 'mttTotal', messageId: 'ticketsGrid.column_TT.label_TT', label: 'TT' },
		{ name: 'mttParcial', messageId: 'ticketsGrid.column_TP.label_TP', label: 'TP' },
		{ name: 'tiempoEnTaller', messageId: 'ticketsGrid.column_tiempoEnTaller.label', label: 'Tiempo en taller' },
		{
			name: 'encuestaSatisfaccion',
			messageId: 'ticketsGrid.column_encuesta.label_encuesta',
			label: 'Satisfacción',
			enabled: flags.encuestaSatisfaccion === true,
		},
		{ name: 'fechaRealizado', messageId: 'ticketsGrid.column_realized.label_realizado', label: 'Realizado' },
		{
			name: 'fechaHoraTurno',
			messageId: 'ticketsGrid.column_state.label_fecha_hora_turno',
			label: 'Turno',
			enabled: flags.fechaHoraTurno === true,
		},
		{
			name: 'abono',
			messageId: 'ticketsGrid.column_abono.label_abono',
			label: 'Abono',
			enabled: flags.abono === true,
		},
		{
			name: 'tipoModelo',
			messageId: 'ticketsGrid.column_tipo_modelo.lable_tipo_modelo',
			label: 'Tipo Modelo',
			enabled: flags.tipoModelo === true,
		},
		{ name: 'acciones', fixed: true },
	];
};
